import React, { useState } from 'react';
import { User } from './type';
import { updateUserStatus } from 'apis/adminServices/userManagmentService';
import { useToastStore } from 'store/toastStore';

interface BlockUserModalProps {
  userDetails: User;
  onClose: () => void;
  onBlockSuccess: () => void;
}

const BlockUserModal: React.FC<BlockUserModalProps> = ({ 
  userDetails, 
  onClose, 
  onBlockSuccess 
}) => {
  const [blockReason, setBlockReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToastStore();

  const handleBlockUser = async () => {
    if (!blockReason.trim()) {
      setError('Please provide a reason for blocking the user.');
      return;
    }
  
    try {
      setLoading(true);
      setError(null);
  
      // API call to block the user
      await updateUserStatus(userDetails.userId, true, blockReason);
  
      showToast('User blocked successfully!', 'error', 3000); // Success toast
      onBlockSuccess(); // Callback after successful block
      onClose(); // Close modal after success
    } catch (error) {
      console.error('Error blocking user:', error);
      setError('Failed to block user. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUnblockUser = async () => {
    try {
      setLoading(true);
  
      // API call to unblock the user
      await updateUserStatus(userDetails.userId, false, 'Unblocked by admin');
  
      showToast('User unblocked successfully!', 'success', 3000); // Success toast
      onBlockSuccess(); // Callback after successful unblock
      onClose(); // Close modal after success
    } catch (error) {
      console.error('Error unblocking user:', error);
      setError('Failed to unblock user. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  

  // Determine if user is active or blocked
  const isActiveUser = userDetails.status === 'Active';

  return (
    <div 
      id="block-user-modal" 
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-black bg-opacity-50"
    >
      <div className="relative p-4 w-full max-w-md">
        <div className="relative bg-white rounded-lg shadow pb-4 min-h-[200px]">
          {/* Modal header */}
          <div className="flex justify-between mx-4 py-4 rounded-t border-b border-lightgray-100">
            <p className="text-base font-semibold text-grease-100">
              {isActiveUser ? 'Block User' : 'Unblock User'}
            </p>
            <button 
              type="button"
              className="text-gray-400 bg-transparent rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
              onClick={onClose}
              disabled={loading}
            >
              <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>
          
          {/* Modal body */}
          {isActiveUser ? (
            <div className="px-4 py-4">
              <p className="text-sm mb-4">
                Blocking this user will restrict their access and prevent further interactions. Please provide a reason for this action.
              </p>
              <div>
                <p className="text-xs text-gray-500">Reason</p>
                <textarea 
                  className="w-full bg-blue-50 mt-2 rounded-lg p-2 min-h-[100px]" 
                  value={blockReason}
                  onChange={(e) => {
                    setBlockReason(e.target.value);
                    setError(null);
                  }}
                  placeholder="Enter a detailed reason for blocking this user"
                  disabled={loading}
                />
                {error && (
                  <p className="text-xs text-red-500 mt-2">{error}</p>
                )}
              </div>
            </div>
          ) : (
            <div className="px-4 py-4 flex flex-col items-center justify-center h-full">
              <p className="text-sm text-center mb-4">
                This user is currently blocked. Do you want to unblock them?
              </p>
            </div>
          )}
          
          {/* Modal footer */}
          <div className="flex gap-4 items-center justify-end px-4 mt-2">
            <button 
              className="flex gap-2 py-3 px-6 bg-blue-500 bg-opacity-25 rounded-md"
              onClick={onClose}
              disabled={loading}
            >
              <p className="font-medium text-lightgray-300 text-xs">Cancel</p>
            </button>
            {isActiveUser ? (
              <button 
                className={`py-3 px-6 rounded-md ${
                  loading || !blockReason.trim()
                    ? 'bg-red-300 cursor-not-allowed' 
                    : 'bg-red-500'
                }`}
                onClick={handleBlockUser}
                disabled={loading || !blockReason.trim()}
              >
                <p className="font-medium text-white text-xs">
                  {loading ? 'Blocking...' : 'Block User'}
                </p>
              </button>
            ) : (
              <button 
                className={`py-3 px-6 rounded-md bg-teal-600 ${
                  loading ? 'cursor-not-allowed' : ''
                }`}
                onClick={handleUnblockUser}
                disabled={loading}
              >
                <p className="font-medium text-white text-xs">
                  {loading ? 'Unblocking...' : 'Unblock User'}
                </p>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockUserModal;